import { useState, useEffect, useContext, createContext } from 'react';

// Feature flag configuration
export const FEATURE_FLAGS = {
  'conversations-v2': {
    enabled: true, // Always enabled for development and demo
    rolloutPercentage: 100, // Gradual rollout: start at 10%, increase to 100%
    conditions: {
      userType: ['admin', 'power_user', 'regular'],
      browser: ['chrome', 'firefox', 'safari', 'edge'],
      device: ['desktop', 'tablet', 'mobile']
    },
    description: 'Revolutionary Conversations Interface with AI-powered features'
  },
  'ai-response-suggestions': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {
      subscription: ['free', 'pro', 'enterprise'] // Include free for demo
    },
    description: 'AI-powered response suggestions in smart composer'
  },
  'mobile-swipe-navigation': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {
      device: ['mobile']
    },
    description: 'Touch gesture navigation for mobile devices'
  },
  'command-palette': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {
      userType: ['regular', 'power_user', 'admin'] // Include regular for demo
    },
    description: 'Linear-style command palette for power users'
  },
  'intelligence-hub': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {
      subscription: ['free', 'pro', 'enterprise'], // Include free for demo
      userType: ['admin', 'power_user', 'regular']
    },
    description: 'AI Intelligence Hub with context-aware analytics'
  },
  'advanced-filtering': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {},
    description: 'PostHog-style advanced filtering system'
  },
  'real-time-sync': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {},
    description: 'Real-time WebSocket synchronization'
  },
  'performance-optimizations': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {},
    description: 'Performance optimizations and virtualization'
  },
  'accessibility-features': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {},
    description: 'Enhanced accessibility compliance features'
  },
  'analytics-dashboard': {
    enabled: true,
    rolloutPercentage: 100,
    conditions: {
      subscription: ['free', 'pro', 'enterprise'] // Include free for demo
    },
    description: 'Advanced analytics and reporting dashboard'
  }
} as const;

export type FeatureFlagName = keyof typeof FEATURE_FLAGS;

interface FeatureFlagContextType {
  isEnabled: (flag: FeatureFlagName) => boolean;
  getFlag: (flag: FeatureFlagName) => FeatureFlagConfig | null;
  refreshFlags: () => Promise<void>;
  flags: Record<string, boolean>;
}

interface FeatureFlagConfig {
  enabled: boolean;
  rolloutPercentage: number;
  conditions: Record<string, readonly string[]>;
  description: string;
}

interface UserContext {
  id: string;
  type: 'admin' | 'power_user' | 'regular';
  subscription: 'free' | 'pro' | 'enterprise';
  email: string;
}

const FeatureFlagContext = createContext<FeatureFlagContextType | null>(null);

// Hash function for consistent user bucketing
function hashUserId(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

// Get device type
function getDeviceType(): 'desktop' | 'tablet' | 'mobile' {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}

// Get browser type
function getBrowserType(): 'chrome' | 'firefox' | 'safari' | 'edge' | 'other' {
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('chrome') && !userAgent.includes('edge')) return 'chrome';
  if (userAgent.includes('firefox')) return 'firefox';
  if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'safari';
  if (userAgent.includes('edge')) return 'edge';
  return 'other';
}

// Get user context (this would typically come from auth context)
function getCurrentUser(): UserContext {
  // This would typically come from your auth system
  return {
    id: localStorage.getItem('userId') || 'anonymous',
    type: (localStorage.getItem('userType') as UserContext['type']) || 'regular',
    subscription: (localStorage.getItem('subscription') as UserContext['subscription']) || 'free',
    email: localStorage.getItem('userEmail') || ''
  };
}

// Feature flag provider component
export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [isLoading, setIsLoading] = useState(true);

  const evaluateFlag = (flagName: FeatureFlagName, config: FeatureFlagConfig): boolean => {
    // Check if flag is globally enabled
    if (!config.enabled) return false;

    // Check rollout percentage
    const user = getCurrentUser();
    const userHash = hashUserId(user.id);
    const bucket = userHash % 100;
    
    if (bucket >= config.rolloutPercentage) return false;

    // Check conditions
    const device = getDeviceType();
    const browser = getBrowserType();

    // User type condition
    if (config.conditions.userType && !config.conditions.userType.includes(user.type)) {
      return false;
    }

    // Subscription condition
    if (config.conditions.subscription && !config.conditions.subscription.includes(user.subscription)) {
      return false;
    }

    // Device condition
    if (config.conditions.device && !config.conditions.device.includes(device)) {
      return false;
    }

    // Browser condition
    if (config.conditions.browser && !config.conditions.browser.includes(browser)) {
      return false;
    }

    return true;
  };

  const refreshFlags = async () => {
    setIsLoading(true);
    
    try {
      // In production, this would fetch from a feature flag service
      const evaluatedFlags: Record<string, boolean> = {};
      
      Object.entries(FEATURE_FLAGS).forEach(([flagName, config]) => {
        evaluatedFlags[flagName] = evaluateFlag(flagName as FeatureFlagName, config);
      });

      // Simulate API call delay in development
      if (import.meta.env.DEV) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setFlags(evaluatedFlags);
      
      // Log flag states for debugging
      if (import.meta.env.DEV) {
        console.log('🚩 Feature flags evaluated:', evaluatedFlags);
      }
      
    } catch (error) {
      console.error('Failed to evaluate feature flags:', error);
      
      // Fallback to safe defaults
      const fallbackFlags: Record<string, boolean> = {};
      Object.keys(FEATURE_FLAGS).forEach(flagName => {
        fallbackFlags[flagName] = false; // Conservative fallback
      });
      setFlags(fallbackFlags);
    } finally {
      setIsLoading(false);
    }
  };

  const isEnabled = (flag: FeatureFlagName): boolean => {
    if (isLoading) return false; // Conservative during loading
    return flags[flag] ?? false;
  };

  const getFlag = (flag: FeatureFlagName): FeatureFlagConfig | null => {
    return FEATURE_FLAGS[flag] || null;
  };

  useEffect(() => {
    refreshFlags();

    // Listen for storage changes (when user logs in/out)
    const handleStorageChange = () => {
      refreshFlags();
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Refresh flags much less frequently (every 15 minutes instead of 5)
    // For a demo/development environment, this is more than sufficient
    const interval = setInterval(refreshFlags, 15 * 60 * 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const contextValue: FeatureFlagContextType = {
    isEnabled,
    getFlag,
    refreshFlags,
    flags
  };

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Hook to use feature flags
export const useFeatureFlag = (flag: FeatureFlagName): boolean => {
  const context = useContext(FeatureFlagContext);
  
  if (!context) {
    console.warn('useFeatureFlag must be used within a FeatureFlagProvider');
    return false;
  }
  
  return context.isEnabled(flag);
};

// Hook to get flag configuration
export const useFeatureFlagConfig = (flag: FeatureFlagName): FeatureFlagConfig | null => {
  const context = useContext(FeatureFlagContext);
  
  if (!context) {
    console.warn('useFeatureFlagConfig must be used within a FeatureFlagProvider');
    return null;
  }
  
  return context.getFlag(flag);
};

// Hook to get all flags (for debugging/admin interfaces)
export const useAllFeatureFlags = (): Record<string, boolean> => {
  const context = useContext(FeatureFlagContext);
  
  if (!context) {
    console.warn('useAllFeatureFlags must be used within a FeatureFlagProvider');
    return {};
  }
  
  return context.flags;
};

// Admin component for feature flag debugging
export const FeatureFlagDebugger: React.FC = () => {
  const flags = useAllFeatureFlags();
  const context = useContext(FeatureFlagContext);
  
  if (import.meta.env.PROD) {
    return null; // Don't show in production
  }

  if (!context) {
    return null;
  }

  const user = getCurrentUser();
  const device = getDeviceType();
  const browser = getBrowserType();

  return (
    <div style={{
      position: 'fixed',
      bottom: 20,
      right: 20,
      background: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      padding: 20,
      borderRadius: 8,
      fontSize: 12,
      fontFamily: 'monospace',
      maxWidth: 400,
      maxHeight: 300,
      overflow: 'auto',
      zIndex: 9999
    }}>
      <h4 style={{ margin: '0 0 10px 0' }}>🚩 Feature Flags Debug</h4>
      
      <div style={{ marginBottom: 10 }}>
        <strong>User Context:</strong><br />
        ID: {user.id}<br />
        Type: {user.type}<br />
        Subscription: {user.subscription}<br />
        Device: {device}<br />
        Browser: {browser}
      </div>
      
      <div>
        <strong>Active Flags:</strong><br />
        {Object.entries(flags).map(([flag, enabled]) => (
          <div key={flag} style={{ 
            color: enabled ? '#4ade80' : '#ef4444',
            margin: '2px 0' 
          }}>
            {enabled ? '✅' : '❌'} {flag}
          </div>
        ))}
      </div>
      
      <button 
        onClick={() => context.refreshFlags()}
        style={{
          marginTop: 10,
          padding: '5px 10px',
          background: '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: 4,
          cursor: 'pointer'
        }}
      >
        Refresh Flags
      </button>
    </div>
  );
};

export default useFeatureFlag; 